/**
 * Application Item Drawer
 * 
 * Side panel for creating/editing applications
 * Features:
 * - Add/Edit mode
 * - Form validation with duplicate name check
 * - Control Activities multi-select picker
 */

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Input, Select, Button, Spin } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../../store';
import { setControlActivities } from '../../../../store/slices/controlActivitiesSlice';
import { getControlActivities } from '../../../../services/controlActivitiesService';
import RightContentDrawer from '../../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../../components/common/ButtonGroup';
import CloseCircleIcon from '../../../../assets/icons/ic-close-circle.svg?react';
import { RiskRanking } from '../../../../services/types';

const { Option } = Select;

// ========== TYPE DEFINITIONS ==========

export interface ApplicationFormData {
  id?: string;
  name: string;
  auditScope: 'In Scope' | 'Out of Scope';
  riskRanking: RiskRanking;
  systemAdministrator: string;
  description?: string;
  controlIds?: string[];
}

interface ApplicationItemDrawerProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: ApplicationFormData | null;
  existingNames?: string[]; // For duplicate name validation
  onClose: () => void;
  onSubmit: (data: ApplicationFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  .ant-form-item {
    margin-bottom: 0 !important;
  }
`;

const FormItem = styled.div`
  margin-bottom: 20px !important;
`;

const FormLabel = styled.label`
  display: block;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #374151;
  margin-bottom: 8px;
`;

const RequiredMark = styled.span`
  color: #DC2626;
  margin-left: 4px;
`;

const StyledInput = styled(Input)`
  height: 44px;
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  
  &:focus, &:hover {
    border-color: #2563EB;
  }
`;

const StyledTextArea = styled(Input.TextArea)`
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  
  &:focus, &:hover {
    border-color: #2563EB;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    min-height: 44px !important;
    border-radius: 8px !important;
    font-family: Inter;
    font-size: 14px;
    padding: 4px 11px !important;
    
    .ant-select-selection-item {
      line-height: 32px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 42px !important;
    }
  }
  
  &:hover .ant-select-selector {
    border-color: #2563EB !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: #2563EB !important;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6B7280;
  font-size: 14px;
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

// Custom Tag Component for Select
const SelectTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #EFF6FF;
  border: none;
  border-radius: 50px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 1;
  color: #1E40AF;
  margin: 2px;
`;

const StyledCloseIcon = styled(CloseCircleIcon)`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  path:first-child {
    fill: #DBEAFE;
  }
  
  path:last-child {
    stroke: #1E40AF;
  }
  
  &:hover {
    opacity: 0.8;
  }
`;


// ========== COMPONENT ==========

const ApplicationItemDrawer: React.FC<ApplicationItemDrawerProps> = ({
  open,
  mode,
  initialValues,
  existingNames = [],
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  
  // Control activities from Redux
  const { controlActivities, lastFetchedReportId } = useSelector(
    (state: RootState) => state.controlActivities
  );
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  
  // Local state for control activities
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([]);
  const [isLoadingControlActivities, setIsLoadingControlActivities] = useState(false);
  
  // Fetch control activities if not in Redux (offline first pattern)
  useEffect(() => {
    const fetchControlActivitiesIfNeeded = async () => {
      if (!open) return;
      if (!selectedReport?.annualReportId) return;
      
      // If control activities are already loaded for this report, skip fetching
      if (controlActivities.length > 0 && lastFetchedReportId === selectedReport.annualReportId) {
        // console.log('✅ Control activities already in Redux, skipping fetch');
        return;
      }

      // Fetch control activities from API
      setIsLoadingControlActivities(true);
      try {
        // console.log('🔄 Fetching control activities for application drawer...');
        const response = await getControlActivities({
          annualReportId: selectedReport.annualReportId,
          sorted: [{name: "ControlCode", direction: "ascending"}],
        });

        dispatch(setControlActivities({
          data: response.data,
          totalCount: response.totalCount,
          reportId: selectedReport.annualReportId,
        }));
        // console.log(`✅ Control activities loaded: ${response.data.length} items`);
      } catch (error) {
        console.error('❌ Failed to fetch control activities:', error);
      } finally {
        setIsLoadingControlActivities(false);
      }
    };

    fetchControlActivitiesIfNeeded();
  }, [open, selectedReport?.annualReportId, controlActivities.length, lastFetchedReportId, dispatch]);
  
  // Reset form when drawer opens/closes or initialValues change
  useEffect(() => {
    if (open) {
      if (initialValues) {
        form.setFieldsValue({
          name: initialValues.name,
          auditScope: initialValues.auditScope,
          riskRanking: initialValues.riskRanking,
          systemAdministrator: initialValues.systemAdministrator,
          description: initialValues.description,
        });
        setSelectedControlIds(initialValues.controlIds || []);
      } else {
        form.resetFields();
        setSelectedControlIds([]);
      }
    }
  }, [open, initialValues, form]);
  
  // Validate duplicate name
  const validateName = (_: unknown, value: string) => {
    if (!value || value.trim() === '') {
      return Promise.reject(new Error('Please enter application name'));
    }
    
    const trimmedValue = value.trim().toLowerCase();
    const currentName = initialValues?.name?.trim().toLowerCase();
    
    // In edit mode, allow the current name
    const isDuplicate = existingNames
      .filter(name => mode === 'edit' ? name.toLowerCase() !== currentName : true)
      .some(name => name.toLowerCase() === trimmedValue);
    
    if (isDuplicate) {
      return Promise.reject(new Error('An application with this name already exists'));
    }
    
    return Promise.resolve();
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      
      const formData: ApplicationFormData = {
        ...values,
        id: initialValues?.id,
        description: values.description || '',
        controlIds: selectedControlIds,
      };
      
      await onSubmit(formData);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Custom tag render for Control Activities
  const renderControlTag = (props: CustomTagProps) => {
    const { label, closable, onClose: onTagClose } = props;
    
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <SelectTag onMouseDown={onPreventMouseDown}>
        {label}
        {closable && (
          <StyledCloseIcon
            onClick={(e) => {
              e.stopPropagation();
              onTagClose();
            }}
          />
        )}
      </SelectTag>
    );
  };
  
  const footer = (
    <ButtonGroup>
      <Button type="default" onClick={onClose} disabled={submitting}>
        Go back
      </Button>
      <Button
        type="primary"
        onClick={handleSubmit}
        loading={submitting}
        disabled={!isAdmin}
      >
        {mode === 'add' ? 'Add item' : 'Save changes'}
      </Button>
    </ButtonGroup>
  );
  
  return (
    <RightContentDrawer
      open={open}
      title={mode === 'add' ? 'Add Application' : 'Edit Application'}
      onClose={onClose}
      footer={footer}
      width={520}
    >
      <FormWrapper>
        <Form form={form} layout="vertical">
          {/* Name */}
          <FormItem>
            <FormLabel>
              Application Name
              <RequiredMark>*</RequiredMark>
            </FormLabel>
            <Form.Item
              name="name"
              rules={[{ validator: validateName }]}
              style={{ marginBottom: 0 }}
            >
              <StyledInput placeholder="e.g., SAP, NetSuite, Oracle" />
            </Form.Item>
          </FormItem>
          
          {/* Audit Scope */}
          <FormItem>
            <FormLabel>
              Audit Scope
              <RequiredMark>*</RequiredMark>
            </FormLabel>
            <Form.Item
              name="auditScope"
              rules={[{ required: true, message: 'Please select audit scope' }]}
              style={{ marginBottom: 0 }}
            >
              <StyledSelect placeholder="Select audit scope">
                <Option value="In Scope">In Scope</Option>
                <Option value="Out of Scope">Out of Scope</Option>
              </StyledSelect>
            </Form.Item>
          </FormItem>
          
          {/* Risk Ranking */}
          <FormItem>
            <FormLabel>
              Risk Ranking
              <RequiredMark>*</RequiredMark>
            </FormLabel>
            <Form.Item
              name="riskRanking"
              rules={[{ required: true, message: 'Please select risk ranking' }]}
              style={{ marginBottom: 0 }}
            >
              <StyledSelect placeholder="Select risk ranking">
                <Option value="Unranked">Unranked</Option>
                <Option value="Low">Low</Option>
                <Option value="Med">Med</Option>
                <Option value="High">High</Option>
              </StyledSelect>
            </Form.Item>
          </FormItem>
          
          {/* System Administrator */}
          <FormItem>
            <FormLabel>
              System Administrator
              <RequiredMark>*</RequiredMark>
            </FormLabel>
            <Form.Item
              name="systemAdministrator"
              rules={[{ required: true, message: 'Please enter system administrator' }]}
              style={{ marginBottom: 0 }}
            >
              <StyledInput placeholder="Name of IT contact or admin" />
            </Form.Item>
          </FormItem>
          
          {/* System Description */}
          <FormItem>
            <FormLabel>System Description</FormLabel>
            <Form.Item
              name="description"
              style={{ marginBottom: 0 }}
            >
              <StyledTextArea
                placeholder="Enter system description"
                rows={4}
                spellCheck={true}
              />
            </Form.Item>
          </FormItem>
          
          {/* Control Activities Picker */}
          <FormItem>
            <FormLabel>Control Activities</FormLabel>
            {isLoadingControlActivities ? (
              <LoadingWrapper>
                <Spin size="small" />
                <span>Loading control activities...</span>
              </LoadingWrapper>
            ) : (
              <>
                <StyledSelect
                  mode="multiple"
                  value={selectedControlIds}
                  onChange={(value) => setSelectedControlIds(value as string[])}
                  placeholder="Select control activities"
                  showSearch
                  filterOption={(input, option) => {
                    const control = controlActivities.find(c => c.id === option?.value);
                    if (!control) return false;
                    const searchText = `${control.controlCode} ${control.controlDescription?.what || ''}`.toLowerCase();
                    return searchText.includes(input.toLowerCase());
                  }}
                  tagRender={renderControlTag}
                  options={controlActivities.map((control) => ({
                    value: control.id,
                    label: `${control.controlCode} - ${control.controlDescription?.what || ''}`,
                  }))}
                  notFoundContent={controlActivities.length === 0 ? 'No control activities available' : 'Not found'}
                />
                <InfoText>
                  Link this application to relevant control activities from EX5
                </InfoText>
              </>
            )}
          </FormItem>
        </Form>
      </FormWrapper>
    </RightContentDrawer>
  );
};

export default ApplicationItemDrawer;
